import { ThemeColor, colors } from "../../framework/theme";
import { TransportOrderProductListItemDto } from "./transportOrderProduct";
import { TransportOrderNotificationListItemDto } from "./transportOrderNotification";
import { OrderSource } from "./orderSource";
import { AttachmentListItemDto } from "../attachment/attachment";

export interface TransportOrderListQueryParameters {
    invoicingState?: TransportOrderInvoicingState;
    invoicingStates?: TransportOrderInvoicingState[];
    senderCustomerId?: string;
    receiverCustomerId?: string;
    billingCustomerId?: string;
    noTransportPlan?: boolean;
    deliveryZipCode?: string;
    deliveryCity?: string;
    pickupZipCode?: string;
    pickupCity?: string;
    pickupTimeFrameStart?: string;
    pickupTimeFrameEnd?: string;
    deliveryTimeFrameStart?: string;
    deliveryTimeFrameEnd?: string;
    timeFrameStartDate?: string;
    timeFrameEndDate?: string;
    keyword?: string;
    selectedOrderStates?: TransportOrderState[];
    page?: number;
    pageSize?: number;
}

export interface TransportOrderListItemDto {
    id: string;
    billingCustomerId?: string;
    billingCustomerName?: string;
    createdDateTime: string;
    createdEmployeeId: string;
    driverComments?: string;
    employeeId?: string;
    employeeName?: string;
    fixedPrice?: number;
    formData?: string;
    formTitle?: string;
    formValues?: string;
    invoiceNewId?: string;
    invoicingState?: TransportOrderInvoicingState;
    latestTransportTaskTypeChangedDateTime?: string;
    modifiedDateTime: string;
    modifiedEmployeeId: string;
    name?: string;
    orderNumber?: number;
    orderTypeId?: string;
    products?: TransportOrderProductListItemDto[];
    receiverBusinessId?: string;
    receiverCustomerId?: string;
    receiverName?: string;
    scheduledEndDateTime?: string;
    scheduledStartDateTime?: string;
    senderBusinessId?: string;
    senderCustomerId?: string;
    senderName?: string;
    state: TransportOrderState;
    totalSum?: number;
    totalSumWithVat?: number;
    transportDetails?: string;
    transportOrderNotifications?: TransportOrderNotificationListItemDto[];
    type?: string;
    vehicleId?: string;
    vehicleName?: string;
    source?: OrderSource;
    attachments?: AttachmentListItemDto[];
}

export interface TransportOrderSaveDto {
    name?: string;
    vehicleId: string;
    employeeId: string;
    scheduledStartDateTime: string;
    scheduledEndDateTime: string;
    state: TransportOrderState;
    transportDetails: string;
    senderBusinessId?: string;
    senderCustomerId: string;
    orderTypeId: string;
    formData?: string;
    formValues?: string;
    receiverBusinessId?: string;
    receiverCustomerId: string;
    driverComments: string;
    billingCustomerId: string;
    fixedPrice: number;
    createdEmployeeId: string;
    modifiedEmployeeId?: string;
    products: { id: string; quantity: number }[];
}

export const getTransportOrderSaveDto = ({
    name,
    vehicleId,
    employeeId,
    scheduledStartDateTime,
    scheduledEndDateTime,
    state,
    transportDetails,
    senderBusinessId,
    senderCustomerId,
    orderTypeId,
    formData,
    formValues,
    receiverBusinessId,
    receiverCustomerId,
    driverComments,
    billingCustomerId,
    fixedPrice,
    createdEmployeeId,
    products,
}: TransportOrderListItemDto): TransportOrderSaveDto => ({
    name,
    vehicleId,
    employeeId,
    scheduledStartDateTime,
    scheduledEndDateTime,
    state,
    transportDetails,
    senderBusinessId,
    senderCustomerId,
    orderTypeId,
    formData,
    formValues,
    receiverBusinessId,
    receiverCustomerId,
    driverComments,
    billingCustomerId,
    fixedPrice,
    createdEmployeeId,
    products,
});

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface TransportOrderDetailsDto extends TransportOrderListItemDto {}

export interface TransportOrderInvoicingStateChangeDto {
    invoicingState: TransportOrderInvoicingState;
}

export enum TransportOrderInvoicingState {
    NotInvoiceable,
    Invoiceable,
    InvoicedInternally,
    AddedToInvoice,
    InvoicedExternally,
}

export enum TransportOrderState {
    OrderCreated = 0,
    PickedUp = 1,
    Delivered = 2,
    Cancelled = 3
}

export const transportOrderStateNames: Readonly<Record<TransportOrderState, string>> = Object.freeze({
    [TransportOrderState.OrderCreated]: "transport.orderState.orderCreated",
    [TransportOrderState.PickedUp]: "transport.orderState.pickedUp",
    [TransportOrderState.Delivered]: "transport.orderState.delivered",
    [TransportOrderState.Cancelled]: "transport.orderState.cancelled"
});

export interface transportOrderStateInfo {
    value: TransportOrderState;
    name: string;
    color: string;
    colorName: ThemeColor;
    colorDark: string;
}

export const transportOrderStates: readonly transportOrderStateInfo[] =
    Object.freeze([
        {
            value: TransportOrderState.OrderCreated,
            name: "transport.orderState.orderCreated",
            color: colors.orange.main,
            colorName: "orange",
            colorDark: colors.orange.dark,
        },
        {
            value: TransportOrderState.PickedUp,
            name: "transport.orderState.pickedUp",
            color: colors.blue.main,
            colorName: "blue",
            colorDark: colors.blue.dark,
        },
        {
            value: TransportOrderState.Delivered,
            name: "transport.orderState.delivered",
            color: colors.green.main,
            colorName: "green",
            colorDark: colors.green.dark,
        },
        {
            value: TransportOrderState.Cancelled,
            name: "transport.orderState.cancelled",
            color: colors.yellow.main,
            colorName: "yellow",
            colorDark: colors.yellow.dark,
        }
    ]);
